import { EditInvestorPageUpdateLegalEntityMutation, InvestorSortBy, InvestorSortDirection } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Label from '@/components/basicComponents/label';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { editInvestorAddInvestorEntityInvestorHouseholdOptions } from '../../constants';
import { CREATE_INVESTOR_MUTATION, EDIT_INVESTOR_PAGE_LIST_INVESTOR_HOUSEHOLDS_QUERY, UPDATE_LEGAL_ENTITY_MUTATION } from '../../queries';
import { EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum, IEditInvestorFormData } from '../../types';
import EditInvestorDeleteHouseholdDialog from './deleteHousehold';
import EditInvestorSuccessDialog from './success';

interface IInvestorHouseholdForm {
  investorHouseholdOption: string;
  familyId: string;
  newHouseholdName: string;
}

const defaultInvestorHouseholdFormValues: IInvestorHouseholdForm = {
  investorHouseholdOption: 'Create a new Investor Household',
  familyId: '',
  newHouseholdName: ''
};

enum DialogTypeEnum {
  DeleteHousehold = 'delete-household',
  Loading = 'loading',
  Success = 'success'
}

interface MoveEntityProps {
  isOpen: boolean;
  entityName: string;
  isLastEntity: boolean;
  editInvestorHouseholdData: IEditInvestorFormData['household'];
  onClose: () => void;
}

const MoveEntity = ({ isOpen, entityName, isLastEntity, editInvestorHouseholdData, onClose }: MoveEntityProps) => {
  const params = useParams();
  const goBack = useGoBack();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { searchParams } = useSearchParamsManager();
  const { control, watch, handleSubmit } = useForm<IInvestorHouseholdForm>({ defaultValues: defaultInvestorHouseholdFormValues });

  const isGroup = searchParams.get('isGroup') === 'true';

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';

  const [investorHouseholdList, setInvestorHouseholdList] = useState<ISelectValue[]>([]);
  const [updatedLegalEntityData, setUpdatedLegalEntityData] = useState<EditInvestorPageUpdateLegalEntityMutation['updateLegalEntity'] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dialogType, setDialogType] = useState<DialogTypeEnum | null>(null);

  const [createInvestor] = useMutation(CREATE_INVESTOR_MUTATION);
  const [updateLegalEntity] = useMutation(UPDATE_LEGAL_ENTITY_MUTATION);

  const { loading: investorHouseholdListLoading } = useQuery(EDIT_INVESTOR_PAGE_LIST_INVESTOR_HOUSEHOLDS_QUERY, {
    skip: !editInvestorHouseholdData?.tenantName,
    variables: {
      data: {
        sortBy: InvestorSortBy.Investor,
        sortDirection: InvestorSortDirection.Asc,
        search: null,
        limit: 1000,
        offset: 0,
        includeRecommendations: true,
        filter: editInvestorHouseholdData?.tenantName
      }
    },
    onCompleted: (data) => {
      const { listInvestors } = data;
      const investorHouseholdListSelectOptions = [
        ...new Set(
          listInvestors?.data
            ?.filter((investor) => investor.id !== investorId)
            ?.map((investor) => createSimpleSelectOption(investor.id, investor.name))
        )
      ];
      setInvestorHouseholdList(investorHouseholdListSelectOptions);
    }
  });

  const navigateToUpdatedEntity = () => {
    const queryParams = new URLSearchParams({
      backToTitle: 'Investors',
      ...(isGroup && { isGroup: 'true' })
    });

    goBack({ fallBack: '/investors', goBackSteps: 2 });
    setTimeout(() => {
      navigate(`/investors/${updatedLegalEntityData?.family?.id}/${updatedLegalEntityData?.id}?${queryParams.toString()}`);
    }, 100);
  };

  const handleMoveEntity = async (data: IInvestorHouseholdForm) => {
    try {
      setDialogType(DialogTypeEnum.Loading);
      const { investorHouseholdOption, familyId, newHouseholdName } = data;

      let newFamilyId: string | null = null;

      if (investorHouseholdOption === EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum.CreateNewInvestorHousehold) {
        const { data: createInvestorData } = await createInvestor({
          variables: {
            data: {
              tenantName: editInvestorHouseholdData?.tenantName,
              name: newHouseholdName || entityName
            }
          }
        });

        newFamilyId = createInvestorData?.createInvestor?.id || null;
      }

      const { data: updateLegalEntityData } = await updateLegalEntity({
        variables: {
          data: {
            id: entityId,
            familyId: newFamilyId || familyId
          }
        }
      });

      setUpdatedLegalEntityData(updateLegalEntityData?.updateLegalEntity);
      setDialogType(DialogTypeEnum.Success);
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      setDialogType(null);
    }
  };

  const onConfirm = (data: IInvestorHouseholdForm) => {
    if (isLastEntity) {
      setDialogType(DialogTypeEnum.DeleteHousehold);
      return;
    }
    handleMoveEntity(data);
  };

  if (dialogType === DialogTypeEnum.Loading) {
    return (
      <ModalWrappedContent isOpen={true} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
        <ModalLoader />
      </ModalWrappedContent>
    );
  }

  if (dialogType === DialogTypeEnum.Success) {
    return (
      <EditInvestorSuccessDialog
        isOpen={isOpen}
        description={
          <>
            {watch('investorHouseholdOption') === EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum.AssignToExisting ? (
              <>
                <b>{entityName || ''}</b> has been removed from {editInvestorHouseholdData?.name || ''} and added to{' '}
                <b>{updatedLegalEntityData?.family?.name || ''}</b>.
              </>
            ) : (
              <>
                <b>{entityName || ''}</b> has been removed from {editInvestorHouseholdData?.name || ''} and added to a new household by itself.
              </>
            )}
          </>
        }
        onClose={navigateToUpdatedEntity}
      />
    );
  }

  if (dialogType === DialogTypeEnum.DeleteHousehold) {
    return <EditInvestorDeleteHouseholdDialog isOpen={isOpen} entityName={entityName} onConfirm={handleSubmit(handleMoveEntity)} onClose={onClose} />;
  }

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Move Entity"
      confirmButton={{
        name: 'Confirm',
        onClick: handleSubmit(onConfirm),
        disabled: false
      }}
    >
      {errorMessage && (
        <ErrorBannerWrapper>
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        </ErrorBannerWrapper>
      )}
      <ModalBody>
        <ModalDescription>
          Create a new household for <b>{entityName || ''}</b> or select an existing one.
        </ModalDescription>
        <InvestorHouseholdWrapper>
          <Label size="md">Investor Household</Label>
          <InvestorHousehold isMobile={isMobile}>
            <Controller
              name="investorHouseholdOption"
              control={control}
              render={({ field: { onChange, value } }) => (
                <OptionsWrapper>
                  {editInvestorAddInvestorEntityInvestorHouseholdOptions.map((option, index) => (
                    <RadioButton
                      key={option + index}
                      name="investorHousehold"
                      radioButtonText={option}
                      selectedOption={value}
                      handleRadioChange={(event) => onChange(event.target.value)}
                    />
                  ))}
                </OptionsWrapper>
              )}
            />
          </InvestorHousehold>
          <InvestorHouseholdSelect>
            <AnimatePresence mode="wait">
              {watch('investorHouseholdOption') === EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum.CreateNewInvestorHousehold && (
                <motion.div key="investorHouseholdInput" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <Controller
                    name="newHouseholdName"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        value={value || ''}
                        onChange={onChange}
                        errorText={error?.message}
                        label="Household Name (optional)"
                        styleType="form"
                        size="md"
                        tooltip="Provide a name for the household or use the entity name as a default."
                      />
                    )}
                  />
                </motion.div>
              )}
              {watch('investorHouseholdOption') === EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum.AssignToExisting && (
                <motion.div key="investorHouseholdSelect" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <Controller
                    name="familyId"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: 'Investor Household is required'
                      }
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        value={value}
                        setValue={onChange}
                        options={investorHouseholdList}
                        errorText={error?.message}
                        label="Investor Household"
                        styleType="form"
                        size="md"
                        width={isMobile ? '100%' : '500px'}
                        loading={investorHouseholdListLoading}
                        withSearch
                        isRequiredField
                      />
                    )}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </InvestorHouseholdSelect>
        </InvestorHouseholdWrapper>
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default MoveEntity;

const ModalBody = styled.div<{ alignItems?: string; gap?: string }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ alignItems }) => alignItems || 'flex-start'};
  gap: ${({ gap }) => gap || '25px'};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-style: normal;
  text-align: center;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  width: 100%;
  margin-bottom: 10px;
`;

const InvestorHouseholdWrapper = styled.div`
  color: ${themeVar('font.base')};
  width: 100%;
`;

const OptionsWrapper = styled.div`
  width: 100%;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
`;

const InvestorHousehold = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: flex-start;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column-reverse' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '0')};
`;

const InvestorHouseholdSelect = styled.div`
  width: 100%;
  margin-top: 10px;
`;
